import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import InternalAd from "@/components/InternalAd";

const REGIONS = ["All", "India", "US", "UK", "Singapore", "China"];

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.max(1, Math.floor((now - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function NewsPage() {
  useSEO({
    title: "Jobs Market Pulse — India, US, UK, Singapore & China | KARM",
    description:
      "Latest jobs and hiring news across India GCCs, US tech, UK, Singapore, and China. Refreshed every 6 hours from top publishers. No fluff — headlines, sources, and direct links.",
    path: "/news",
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("All");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = region === "All" ? { limit: 15 } : { limit: 15, region };
    api
      .get("/news/latest", { params })
      .then((r) => alive && setItems(r.data.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [region]);

  return (
    <div className="karm-editorial min-h-screen" data-testid="news-page">
      <section className="px-6 py-16 max-w-5xl mx-auto">
        <p className="small-caps mb-3">— Jobs Market Pulse —</p>
        <h1
          className="font-serif italic text-4xl md:text-5xl tracking-tight mb-3"
          style={{ color: "var(--karm-ink)" }}
        >
          The market, in a single glance.
        </h1>
        <p
          className="font-serif text-base md:text-lg leading-relaxed mb-8 max-w-2xl"
          style={{ color: "var(--karm-ink-muted)" }}
        >
          Refreshed every 6 hours from top publishers across India, the US, the UK, Singapore, and
          China — hiring, layoffs, GCC expansion, key innovation signals. Headlines only; click
          through for the full read at the source.
        </p>

        {/* Region tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="news-region-tabs">
          {REGIONS.map((r) => {
            const active = region === r;
            return (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className="px-3 py-1.5 text-xs border transition-colors"
                style={{
                  borderColor: "var(--karm-paper-edge)",
                  color: active ? "var(--karm-vermillion)" : "var(--karm-ink-muted)",
                  background: active ? "var(--karm-ivory-warm)" : "transparent",
                  fontWeight: active ? 600 : 400,
                }}
                data-testid={`news-region-${r.toLowerCase()}`}
              >
                {r}
              </button>
            );
          })}
        </div>

        {/* Internal ad — News page top */}
        <div className="mb-8">
          <InternalAd slot="news_top" />
        </div>

        {loading ? (
          <div
            className="py-20 text-center font-serif italic"
            style={{ color: "var(--karm-ink-muted)" }}
            data-testid="news-loading"
          >
            ⸻ Reading the presses ⸻
          </div>
        ) : items.length === 0 ? (
          <div
            className="py-20 text-center font-serif italic"
            style={{ color: "var(--karm-ink-muted)" }}
            data-testid="news-empty"
          >
            ⸻ Nothing new here yet. Check back shortly. ⸻
          </div>
        ) : (
          <ol
            className="border-t"
            style={{ borderColor: "var(--karm-paper-edge)" }}
            data-testid="news-list"
          >
            {items.map((n, i) => (
              <li
                key={n.link || i}
                className="border-b py-5"
                style={{ borderColor: "var(--karm-paper-edge)" }}
                data-testid={`news-item-${i}`}
              >
                <a
                  href={n.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div
                    className="small-caps mb-2 flex items-center gap-3"
                    style={{ color: "var(--karm-vermillion)" }}
                  >
                    <span>{n.region}</span>
                    <span style={{ color: "var(--karm-ink-muted)" }}>·</span>
                    <span style={{ color: "var(--karm-ink-muted)" }}>{n.source}</span>
                    <span style={{ color: "var(--karm-ink-muted)" }}>·</span>
                    <span style={{ color: "var(--karm-ink-muted)" }}>{timeAgo(n.published_at)}</span>
                  </div>
                  <h2
                    className="font-serif text-xl md:text-2xl leading-snug group-hover:underline"
                    style={{ color: "var(--karm-ink)" }}
                  >
                    {n.title}
                    <ArrowUpRight
                      className="inline-block ml-2 h-4 w-4 opacity-60 group-hover:opacity-100"
                      style={{ verticalAlign: "middle" }}
                    />
                  </h2>
                  {n.summary && n.summary !== n.title && (
                    <p
                      className="font-serif text-sm md:text-base leading-relaxed mt-2 max-w-3xl"
                      style={{ color: "var(--karm-ink-muted)" }}
                    >
                      {n.summary}
                    </p>
                  )}
                </a>
              </li>
            ))}
          </ol>
        )}

        <p
          className="mt-10 font-serif italic text-xs"
          style={{ color: "var(--karm-ink-muted)" }}
        >
          News items retained for 7 days. Curated automatically from public news feeds — no
          editorial endorsement. For deeper analysis of India GCC hiring trends, browse{" "}
          <Link to="/jobs" className="underline">15,000+ live GCC roles</Link> or the{" "}
          <Link to="/companies" className="underline">1,100+ GCC employer directory</Link>.
        </p>
      </section>
    </div>
  );
}
